import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  ArrowRight, 
  Brain, 
  HeartHandshake, 
  Activity,
  Clock,
  CheckCircle,
  Sparkles,
  Shield,
  CreditCard,
  DollarSign,
  Package,
  BookOpen,
  Quote
} from "lucide-react";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";
import KetamineSavingsPlan from "@/components/KetamineSavingsPlan";
import CareCreditBanner from "@/components/CareCreditBanner";

const ServicesPage = () => {
  const seoOverride = usePageSeo("services");
  const treatments = [
    {
      icon: HeartHandshake,
      title: "Ketamine-Assisted Psychotherapy",
      subtitle: "Transformative Healing",
      description: "Combine the neuroplasticity benefits of ketamine with guided psychotherapy for deeper psychological healing and lasting behavioral change. This innovative approach helps process trauma more effectively and develop new neural pathways.",
      benefits: [
        "Enhanced therapeutic breakthroughs",
        "Process trauma more effectively",
        "Develop new neural pathways",
        "Integration support included",
        "Compassionate, trauma-informed care",
      ],
      duration: "Sessions tailored to your needs",
    },
  ];

  const conditions = [
    { name: "Treatment-Resistant Depression", icon: Brain },
    { name: "Anxiety Disorders", icon: Sparkles },
    { name: "Post-Traumatic Stress Disorder", icon: Shield },
    { name: "Trauma", icon: HeartHandshake },
    { name: "Mood Disorders", icon: Activity },
  ];

  const process = [
    {
      step: "01",
      title: "Free Consultation",
      description: "Meet with our team to discuss your history, goals, and determine if ketamine therapy is appropriate for you.",
    },
    {
      step: "02",
      title: "Medical Evaluation",
      description: "Complete medical screening including review of medications, health history, and any contraindications.",
    },
    {
      step: "03",
      title: "Personalized Plan",
      description: "Receive a customized treatment plan with recommended protocol, session frequency, and expected outcomes.",
    },
    {
      step: "04",
      title: "Treatment Sessions",
      description: "Begin your treatment series in the comfort and privacy of your home with continuous monitoring through telemedicine sessions.",
    },
    {
      step: "05",
      title: "Ongoing Support",
      description: "Regular check-ins and maintenance sessions to sustain your progress and optimize results.",
    },
  ];

  return (
    <div className="pt-20" data-testid="services-page">
      <SEO
        title="Psychedelic-Assisted Psychotherapy Through Ketamine"
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        description={seoOverride?.meta_description || "Trauma-informed Ketamine-Assisted Psychotherapy for depression, anxiety, PTSD, and trauma. Personalized treatment plans with Laura A. Jeanis, LCSW, LICSW, CCTP, CEO."}
        imageAlt={seoOverride?.image_alt}
        pathname="/services"
      />
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30 relative overflow-hidden">
        <div className="subtle-glow absolute inset-0" />
        <div className="max-w-7xl mx-auto relative">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
                Virtual Ketamine-Assisted Therapy For Women With Trauma
              </h1>
              <h2 className="mt-5 font-serif text-xl sm:text-2xl font-semibold text-foreground/90 leading-snug">
                Specialized In Helping Hyper-Independent Women Through The Power of Ketamine-Assisted Therapy
              </h2>

              {/* Science of Serenity Narrative Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="mt-8 rounded-3xl bg-white/90 backdrop-blur border border-primary/15 shadow-soft overflow-hidden"
                data-testid="services-science-of-serenity-section"
              >
                <figure className="w-full">
                  <img
                    src="https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/gkoy9b5z_IMG_5932.jpeg"
                    alt="Serene wellness, inner peace, and safety after overcoming hyper-independence through Ketamine-Assisted Therapy"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                    data-testid="services-science-of-serenity-image"
                  />
                  <figcaption className="px-5 md:px-8 py-3 text-xs md:text-sm italic text-muted-foreground border-b border-primary/10">
                    Serene wellness, inner peace, and safety after overcoming hyper-independence through Ketamine-Assisted Therapy.
                  </figcaption>
                </figure>

                <div className="px-5 md:px-8 py-8 md:py-10 space-y-5 text-base text-muted-foreground leading-relaxed">
                  <p>
                    For those who have learned to survive by being fiercely self-reliant, the concept of dependence can feel like an existential threat. This protective mechanism, often labeled '
                    <Link
                      to="/specialties/therapy-for-hyper-independence"
                      className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors"
                      data-testid="services-science-hyperindependence-link"
                    >
                      hyper-independence
                    </Link>
                    ,' is frequently a response to relational trauma or an environment where we felt fundamentally unsafe or unsupported.
                  </p>
                  <p>
                    When you have internalized the belief that &ldquo;I can only rely on myself&rdquo; or that &ldquo;vulnerability equals danger,&rdquo; opening up to another person isn't just difficult; it feels like dismantling your entire defense system. Your body and mind may treat intimacy as a signal to withdraw, creating an internal barrier that keeps others at bay, even when your deepest self craves connection.
                  </p>
                  <p>
                    The core of this struggle is not a rejection of love, but a profound and often unconscious need for safety. When negative core beliefs&mdash;such as &ldquo;I am unworthy,&rdquo; &ldquo;I will be abandoned,&rdquo; or &ldquo;It&rsquo;s not safe to let people in&rdquo;&mdash;run the script, the nervous system remains on high alert. To find peace, you must first acknowledge that your hyper-independence was a solution, not a flaw. Healing begins with a process of relational rewiring, intentionally teaching your nervous system to tolerate closeness. This isn't achieved through intellectualizing, but through felt experiences of safety that contradict those old, painful narratives.
                  </p>
                  <p>
                    Discovering inner peace requires navigating this tension between the old need for armor and the new desire for connection. It means recognizing that you can be both strong and soft, and that true power lies in the capacity to discern when to protect yourself and when to connect. As you slowly dismantle the belief that you must be entirely self-contained, you allow yourself to lean on others without collapsing into them. This balance transforms relationships from a constant source of anxiety into a container for shared strength and mutual growth.
                  </p>
                  <p>
                    Ultimately, safety in relationships is an internal job that reflects an external reality. It is about finding relationships where you are met with respect, consistency, and attunement, allowing you to gradually practice letting your guard down. When your past tells you that people cannot be trusted, you create a new story by allowing yourself small, manageable, and repeated experiences of being seen and held in your vulnerability. In doing so, you build a sanctuary both within yourself and within your connection, proving that it is finally safe to let people in.
                  </p>

                  <div className="pt-4 border-t border-primary/10 space-y-4">
                    <p className="text-foreground font-medium">
                      Ready to start rewiring your nervous system for connection? You don't have to navigate this healing journey alone.
                    </p>
                    <p>
                      Schedule your free consultation today to learn how Ketamine-Assisted Therapy can help you break free from survival mode and build safe, lasting relationships.
                    </p>
                    <Button
                      asChild
                      size="lg"
                      className="rounded-full"
                      data-testid="services-science-of-serenity-cta"
                    >
                      <Link to="/contact">
                        Schedule Your Free Consultation
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </motion.div>

              {/* Therapist Photo — inline above Laura intro */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="mt-10 flex flex-col items-center"
                data-testid="therapist-portrait"
              >
                <div className="rounded-3xl overflow-hidden shadow-soft w-full max-w-sm">
                  <img
                    src="https://customer-assets.emergentagent.com/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/l4ja8zkq_IMG_4558_Facetune_17-01-2023-21-22-22.jpeg"
                    alt="Laura A. Jeanis, LCSW, LICSW, CCTP, CEO"
                    className="w-full h-auto object-cover"
                    data-testid="therapist-portrait-img"
                  />
                </div>
                <div className="text-center mt-5">
                  <p className="font-serif text-xl font-semibold text-foreground">
                    Laura A. Jeanis
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    LCSW, LICSW, CCTP, CEO
                  </p>
                  <p className="text-xs text-primary font-medium mt-1 italic">
                    Licensed Psychotherapist · Certified Clinical Trauma Professional
                  </p>
                </div>
              </motion.div>

              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                Laura A. Jeanis, LCSW, LICSW, CCTP, CEO offers compassionate Ketamine-Assisted
                Psychotherapy designed to address trauma, depression, and anxiety. Every
                treatment plan is personalized to your unique needs and goals.
              </p>
              
              {/* Session Duration Info */}
              <div className="mt-8 p-6 bg-white/80 rounded-2xl border border-primary/20">
                <div className="flex items-start gap-3">
                  <Clock className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Individual Session Durations</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><span className="font-medium text-foreground">Full Dosing Sessions:</span> 2 hours long</li>
                      <li><span className="font-medium text-foreground">Full-Dosing Integration Sessions:</span> 1 hour long</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founder Quote */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-white" data-testid="services-founder-quote-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="text-center"
            data-testid="services-founder-quote"
          >
            <Quote className="h-10 w-10 md:h-12 md:w-12 text-primary/30 mx-auto mb-8" strokeWidth={1.5} />
            <blockquote className="font-serif italic text-2xl sm:text-3xl md:text-4xl leading-relaxed text-foreground">
              &ldquo;I help high-achieving, hyper-independent women use Ketamine-Assisted
              Psychotherapy to rewire their nervous system for safety &mdash; so they can stop
              believing it&rsquo;s unsafe to let people in and finally build connected,
              lasting relationships.&rdquo;
            </blockquote>
            <div className="mt-10 flex items-center justify-center gap-4">
              <span className="h-px w-8 bg-primary/40" />
              <cite className="not-italic text-xs sm:text-sm font-medium tracking-[0.2em] uppercase text-muted-foreground">
                Laura A. Jeanis, LCSW, LICSW, CCTP
              </cite>
              <span className="h-px w-8 bg-primary/40" />
            </div>

            {/* Service areas box — appears directly below the founder quote */}
            <div
              className="mt-14 max-w-3xl mx-auto rounded-3xl border border-primary/15 bg-white/90 backdrop-blur shadow-soft px-6 md:px-10 py-8 md:py-10 text-left"
              data-testid="services-areas-box"
            >
              <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-semibold text-foreground leading-snug text-center">
                Offering Virtual Ketamine-Assisted Therapy in these spots:
              </h2>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-base md:text-lg">
                <li>
                  <Link
                    to="/specialties/online-ketamine-therapy-women-miami"
                    className="inline-flex items-center gap-2 text-primary font-medium underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
                    data-testid="services-area-link-miami"
                  >
                    Miami, Florida
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/specialties/ketamine-therapy-hyper-independent-women-cumberland-maine"
                    className="inline-flex items-center gap-2 text-primary font-medium underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
                    data-testid="services-area-link-cumberland"
                  >
                    Cumberland, Maine
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/specialties/ketamine-therapy-hyper-independent-women-portland-maine"
                    className="inline-flex items-center gap-2 text-primary font-medium underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
                    data-testid="services-area-link-portland"
                  >
                    Portland, Maine
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
                <li>
                  <Link
                    to="/specialties/ketamine-therapy-hyper-independent-women-washington-dc"
                    className="inline-flex items-center gap-2 text-primary font-medium underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
                    data-testid="services-area-link-dc"
                  >
                    Washington, D.C.
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 px-6 md:px-12 border-b border-border/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-serif text-2xl font-semibold text-foreground">
              Conditions We Treat
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {conditions.map((condition, index) => (
              <motion.div
                key={condition.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-2 px-5 py-3 bg-white rounded-full border border-border/40 shadow-sm">
                  <condition.icon className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">{condition.name}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Types */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Our Approach
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Trauma-informed, compassionate care designed for lasting transformation.
            </p>
          </motion.div>

          <div className="space-y-8">
            {treatments.map((treatment, index) => (
              <motion.div
                key={treatment.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-0 overflow-hidden bg-white border-border/40 rounded-3xl shadow-sm hover:shadow-soft transition-shadow">
                  <div className="grid grid-cols-1 lg:grid-cols-3">
                    <div className="p-10 lg:col-span-2">
                      <div className="flex items-start gap-4 mb-6">
                        <div className="p-3 rounded-full bg-secondary">
                          <treatment.icon className="h-7 w-7 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-serif text-2xl font-semibold text-foreground">
                            {treatment.title}
                          </h3>
                          <p className="text-accent font-medium">{treatment.subtitle}</p>
                        </div>
                      </div>
                      <p className="text-muted-foreground leading-relaxed mb-6">
                        {treatment.description}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{treatment.duration}</span>
                      </div>
                    </div>
                    <div className="bg-secondary/50 p-10">
                      <h4 className="font-semibold text-foreground mb-4">Key Benefits</h4>
                      <ul className="space-y-3">
                        {treatment.benefits.map((benefit) => (
                          <li key={benefit} className="flex items-start gap-3">
                            <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-muted-foreground">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Process */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Your Treatment Journey
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              We guide you through every step of the process, from initial consultation 
              to ongoing support.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <Card className="p-6 h-full bg-white border-0 rounded-2xl text-center">
                  <span className="font-serif text-4xl font-bold text-accent/50">{step.step}</span>
                  <h3 className="font-semibold text-foreground mt-3 mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </Card>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="h-5 w-5 text-accent" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                q: "How does ketamine tablet therapy work?",
                a: "Ketamine tablets (sublingual lozenges) dissolve under your tongue and are absorbed into your bloodstream. This method allows you to receive treatment in the comfort of your own home via telemedicine, while still being monitored by your therapist throughout the session.",
              },
              {
                q: "How many treatments will I need?",
                a: "Treatment plans are personalized to your needs. Many clients begin with a series of sessions over several weeks, followed by maintenance sessions as needed. Your treatment frequency will be adjusted based on your individual response and therapeutic goals.",
              },
              {
                q: "What are the side effects?",
                a: "Common temporary effects include mild dissociation, drowsiness, and occasionally nausea. These typically resolve within a couple hours after the session. You'll be monitored throughout your telemedicine session and should have a support person available at home.",
              },
            ].map((faq, index) => (
              <motion.div
                key={faq.q}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 bg-white border-border/40 rounded-xl">
                  <h3 className="font-semibold text-foreground mb-2">{faq.q}</h3>
                  <p className="text-muted-foreground">{faq.a}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Session Packages Callout */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Intensives For Your <span className="italic text-primary">Unique Ketamine Journey</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Five carefully crafted containers — from a foundational 8-week intensive to an all-in 20-week transformation. Each one designed to meet you exactly where you are.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10"
          >
            {[
              { label: "Foundation", title: "8 Weeks Intensive", hours: "10 Individual Sessions ~ 12 Meeting Hours" },
              { label: "All-In", title: "16 Weeks All-In Intensive", hours: "22 Individual Sessions ~ 29 Meeting Hours" },
              { label: "All-In", title: "20 Weeks Advanced Inner Transformation", hours: "28 Individual Sessions ~ 36 Meeting Hours" },
              { label: "Continuation", title: "12 Weeks Intensive", hours: "18 Individual Sessions ~ 24 Meeting Hours" },
              { label: "Group", title: "9-Week Virtual KAP Group Immersion", hours: "9 Psychotherapy Sessions ~ 14.5 Meeting Hours" },
            ].map((p) => (
              <Card key={p.title} className="p-6 bg-white border-border/40 rounded-2xl text-center">
                <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  {p.label}
                </span>
                <h3 className="font-serif text-lg font-semibold text-foreground mt-3">{p.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{p.hours}</p>
              </Card>
            ))}
          </motion.div>

          {/* Extended Payment Plan Callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-10 max-w-3xl mx-auto"
            data-testid="services-payment-plan-callout"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5 rounded-2xl border border-primary/30 bg-primary/5 px-5 py-4">
              <div className="flex items-start gap-4 flex-1">
                <CreditCard className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm uppercase tracking-[0.18em] text-primary font-semibold">
                    Extended Payment Plans Available
                  </p>
                  <p className="mt-1 font-serif text-lg md:text-xl text-foreground">
                    For as low as <span className="font-semibold text-primary">$125 bi-weekly</span>.
                  </p>
                </div>
              </div>
              <a
                href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto flex-shrink-0"
              >
                <Button
                  className="w-full sm:w-auto rounded-full px-6 py-5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="services-payment-plan-cta"
                >
                  Book Free 40-min Call
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </motion.div>

          <div className="text-center">
            <Link to="/fees">
              <Button
                size="lg"
                className="rounded-full px-10 py-6 btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="psychotherapy-link-btn"
              >
                See Full Intensive Details
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-12 max-w-2xl mx-auto"
          >
            <Card className="p-6 bg-white/80 border-border/40 rounded-xl">
              <div className="flex items-start gap-4">
                <CreditCard className="h-6 w-6 text-primary flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-foreground">Payment Methods</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    I accept all cards. Payments are made through HIPAA-compliant billing services.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Ketamine Intensive Savings Plan — teaser (canonical article lives on /fees) */}
      <KetamineSavingsPlan background="bg-white" variant="teaser" />

      {/* CareCredit Financing Banner */}
      <CareCreditBanner variant="muted" />

      {/* Services Summary Section */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Our Services
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <Card className="h-full p-8 bg-white border-primary/20 rounded-3xl relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary to-accent"></div>
              
              <div className="text-center mb-6">
                <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Brain className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-foreground">
                  Psychedelic-Assisted Psychotherapy <span className="italic text-primary">through Ketamine</span>
                </h3>
                <p className="mt-4 text-muted-foreground">
                  Transformative healing combining the neuroplasticity benefits of ketamine with guided psychotherapy for deeper psychological healing and lasting behavioral change.
                </p>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-primary" />
                  <span><strong>Full Dosing:</strong> 2 hours</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-primary" />
                  <span><strong>Full-Dosing Integration:</strong> 1 hour</span>
                </div>
              </div>
              
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                <span className="px-3 py-1 bg-secondary/50 rounded-full text-xs font-medium">Depression</span>
                <span className="px-3 py-1 bg-secondary/50 rounded-full text-xs font-medium">Anxiety</span>
                <span className="px-3 py-1 bg-secondary/50 rounded-full text-xs font-medium">PTSD</span>
                <span className="px-3 py-1 bg-secondary/50 rounded-full text-xs font-medium">Trauma</span>
              </div>
              
              <div className="text-center">
                <a href="/fees">
                  <Button 
                    size="lg" 
                    className="rounded-full px-10 py-6 btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="psychotherapy-link-btn"
                  >
                    View Intensive Options
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </Card>
          </motion.div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">
              Ready to Explore Your Options?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
              Schedule a free consultation to discuss which treatment approach 
              may be right for your unique situation.
            </p>
            <div className="mt-10">
              <a href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting" target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  className="rounded-full px-10 py-6 text-lg btn-scale shadow-soft bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="services-cta"
                >
                  Schedule Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>

            <div className="mt-8">
              <Link
                to="/blog"
                className="group inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
                data-testid="services-blog-link"
              >
                <BookOpen className="h-4 w-4" />
                <span>Curious how ketamine actually works? Read more on the blog</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
